import { ThemeEvents, CartUpdateEvent, CartGroupedSections } from "@theme/events";

const source = "lumeia-cart-gifts";
const componentSelector = "cart-items-component";
let lastCart = null;
let running = false;
let pending = false;
let frame = 0;
let warned = false;
// Bundle ids whose paid gifts still need removing; kept until a removal succeeds.
const pendingDrops = new Set();
const retryDelays = [500, 1000, 2000];
const observed = new WeakSet();
const readonlyInputs = new WeakSet();

function metadata(item) {
  try {
    const value = JSON.parse(item.properties?.__kaching_bundles);
    return value && typeof value.id === "string" && value.id ? value : null;
  } catch {
    return null;
  }
}

function boxTotals(cart) {
  const totals = new Map();
  for (const item of cart.items) {
    const bundle = metadata(item);
    if (bundle && !Object.hasOwn(bundle, "gift")) {
      totals.set(bundle.id, (totals.get(bundle.id) || 0) + item.quantity);
    }
  }
  return totals;
}

function bundleItem(element) {
  if (!lastCart) return null;
  const row = element.closest(componentSelector + " tr[data-key]");
  const item = row && lastCart.items.find((item) => item.key === row.dataset.key);
  return item && metadata(item) ? item : null;
}

function markRows() {
  frame = 0;
  if (!lastCart) return;
  const items = new Map(lastCart.items.map((item) => [item.key, item]));
  document.querySelectorAll(componentSelector + " tr[data-key]").forEach((row) => {
    const item = items.get(row.dataset.key);
    const bundle = !!item && !!metadata(item);
    if (row.hasAttribute("data-lumeia-bundle") !== bundle) {
      row.toggleAttribute("data-lumeia-bundle", bundle);
    }
    row.querySelectorAll(".quantity-input").forEach((input) => {
      if (bundle) {
        if (!input.readOnly) {
          input.readOnly = true;
          readonlyInputs.add(input);
        }
        // Enabled readonly inputs remain in the cart form's updates[].
        if (input.disabled) input.disabled = false;
        if (input.value !== String(item.quantity)) input.value = String(item.quantity);
      } else if (readonlyInputs.has(input)) {
        input.readOnly = false;
        readonlyInputs.delete(input);
      }
    });
  });
}

function scheduleRows() {
  if (!frame) frame = requestAnimationFrame(markRows);
}

function watchComponents() {
  document.querySelectorAll(componentSelector).forEach((component) => {
    if (observed.has(component)) return;
    observed.add(component);
    new MutationObserver(scheduleRows).observe(component, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-lumeia-bundle", "readonly"],
    });
  });
  scheduleRows();
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withRetry(task) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await task(attempt);
    } catch (error) {
      if (attempt >= retryDelays.length) throw error;
      await wait(retryDelays[attempt]);
    }
  }
}

async function readCart(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) throw new Error("Cart request failed");
  const cart = await response.json();
  if (!Array.isArray(cart.items) || cart.errors) throw new Error("Cart response failed");
  return cart;
}

function paidGifts(cart) {
  return cart.items.filter((item) => {
    const bundle = metadata(item);
    return bundle && pendingDrops.has(bundle.id) && Object.hasOwn(bundle, "gift") && item.final_line_price > 0;
  });
}

async function removePaidGifts(cart) {
  if (!paidGifts(cart).length) {
    pendingDrops.clear();
    return;
  }
  const components = [...document.querySelectorAll(componentSelector)];
  components.forEach((component) => component.classList.add("cart-items-disabled"));
  try {
    // A retry reads the cart again, so it never sends line keys a failed attempt may have changed.
    const newCart = await withRetry(async (attempt) => {
      const current = attempt ? await readCart(FoxTheme.routes.cart, { headers: { Accept: "application/json" } }) : cart;
      const gifts = paidGifts(current);
      // An earlier attempt's removal landed: keep this cart; the theme redraws its own sections.
      if (!gifts.length) return current;
      // Use fresh line keys: a bought accessory may share a gift's variant.
      if (gifts.some((item) => typeof item.key !== "string" || !item.key)) throw new Error("Cart key missing");
      const sections = [];
      document.dispatchEvent(new CartGroupedSections(sections));
      return readCart(FoxTheme.routes.cart_update_url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          updates: Object.fromEntries(gifts.map((item) => [item.key, 0])),
          sections: [...new Set(sections)].join(","),
          sections_url: window.location.pathname,
        }),
      });
    });
    pendingDrops.clear();
    lastCart = newCart;
    document.dispatchEvent(new CartUpdateEvent(newCart, source, {
      itemCount: newCart.item_count,
      source,
      sections: newCart.sections,
    }));
  } finally {
    components.forEach((component) => component.classList.remove("cart-items-disabled"));
    document.querySelectorAll(componentSelector).forEach((component) => component.classList.remove("cart-items-disabled"));
    scheduleRows();
  }
}

async function refresh() {
  pending = true;
  if (running) return;
  running = true;
  try {
    while (pending) {
      pending = false;
      // Native updates emit before morphing; let them finish before owning the busy state.
      while (document.querySelector(`${componentSelector}.cart-items-disabled`)) {
        await new Promise(requestAnimationFrame);
      }
      const cart = await withRetry(() => readCart(FoxTheme.routes.cart, { headers: { Accept: "application/json" } }));
      // A native change during the read makes this snapshot stale.
      if (pending || document.querySelector(`${componentSelector}.cart-items-disabled`)) {
        pending = true;
        continue;
      }
      const before = lastCart ? boxTotals(lastCart) : new Map();
      const after = boxTotals(cart);
      for (const [id, count] of before) {
        if ((after.get(id) || 0) < count) pendingDrops.add(id);
      }
      // A charged gift with no box left in its bundle is pending too, so a later page retries a failed removal.
      for (const item of cart.items) {
        const bundle = metadata(item);
        if (bundle && Object.hasOwn(bundle, "gift") && item.final_line_price > 0 && !after.has(bundle.id)) {
          pendingDrops.add(bundle.id);
        }
      }
      lastCart = cart;
      scheduleRows();
      await removePaidGifts(cart);
    }
  } catch {
    pending = false;
    if (!warned) {
      warned = true;
      console.warn("Lumeia cart gifts: cart check failed; no further changes were made.");
    }
  } finally {
    running = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source !== source) void refresh();
});

window.addEventListener("click", (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest(".quantity-plus, .quantity-minus");
  if (!button || !bundleItem(button)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}, true);

function guardBundleQuantity(event) {
  if (!(event.target instanceof HTMLInputElement) || !event.target.matches(".quantity-input")) return;
  if (event.type === "keydown" && event.key !== "Enter") return;
  const item = bundleItem(event.target);
  if (!item) return;
  event.target.value = String(item.quantity);
  event.preventDefault();
  event.stopImmediatePropagation();
}

for (const eventName of ["blur", "change", "keydown"]) {
  window.addEventListener(eventName, guardBundleQuantity, true);
}

function start() {
  watchComponents();
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) =>
      node instanceof Element && (node.matches(componentSelector) || node.querySelector(componentSelector))
    ))) watchComponents();
  }).observe(document.body, { childList: true, subtree: true });
  if (document.querySelector(`${componentSelector} tr[data-key]`)) void refresh();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
else start();
